// Getting-started tour copy — the approved implementation copy, moved
// verbatim from the /design/tour prototype.

export const WELCOME_FORK = {
  title: 'Let\u2019s get started',
  subtitle: '',
  demo: {
    title: 'Take a tour',
    body: 'We’ll walk you through a demo space so you can learn how Pitchcrow works.',
    cta: 'Recommended',
  },
  own: {
    title: 'Start from scratch',
    body: 'Create a space and upload your own files. The demo stays in your sidebar.',
    cta: 'Skip for now',
  },
};

export interface CoachmarkCopy {
  step: number;
  title: string;
  body: string;
  cta: string;
}

export const TOTAL_STEPS = 7;

export const COACHMARKS = {
  demoSpace: {
    step: 1,
    title: 'Your deal\u2019s data room',
    body: 'A space holds everything for one deal: files, models, decks, and the agent that works on them.',
    cta: 'Next',
  },
  dataRoom: {
    step: 2,
    title: 'The deal’s files',
    body: 'Financials, market studies, and deal docs — all in this space. Pitchcrow reads every file.',
    cta: 'Next',
  },
  openModel: {
    step: 3,
    title: 'Let’s build the model',
    body: 'Double-click to open Project Alpine — Valuation Model.',
    cta: 'Next',
  },
  sheetPrompt: {
    step: 3,
    title: 'Ask Pitchcrow to build it',
    body: 'We drafted the prompt for the valuation model. Press Send to see it in action.',
    cta: 'Send',
  },
  sheetDone: {
    step: 3,
    title: 'Pitchcrow created the valuation model',
    body: 'It built four sheets: assumptions, trading comps, DCF, and the football field.',
    cta: 'Next',
  },
  backToSpace: {
    step: 4,
    title: 'Now let’s build the CIM',
    body: 'Head back to Project Alpine.',
    cta: 'Next',
  },
  openCim: {
    step: 4,
    title: 'Open the CIM',
    body: 'Double-click to open Project Alpine — CIM.',
    cta: 'Next',
  },
  deckPrompt: {
    step: 4,
    title: 'Now let’s build the CIM',
    body: 'This prompt asks Pitchcrow to use the data room and your model to build the CIM. Press Send.',
    cta: 'Send',
  },
  deckDone: {
    step: 4,
    title: 'The CIM is ready',
    body: 'Thirty-five slides, built from the data room and your model.',
    cta: 'Next',
  },
  commentsIntro: {
    step: 5,
    title: 'The deal team left comments',
    body: 'Two comments from your MD are pinned to the slide. Hover over them to view, and click a thread to open it.',
    cta: 'Next',
  },
  commentsPrompt: {
    step: 5,
    title: 'Ask Pitchcrow to resolve them',
    body: 'Pitchcrow reads the comments, reworks the slide, and resolves the threads.',
    cta: 'Send',
  },
  // The {key} placeholder is swapped for the platform's key (Option/Alt) by
  // the driver at render time.
  voiceIntro: {
    step: 6,
    title: 'Talk to Pitchcrow',
    body: 'Tap the left {key} key twice and say what you want changed. Tap it once to stop.',
    cta: 'Try it later',
  },
  chromeShare: {
    step: 7,
    title: 'Share',
    body: 'Everyone in the space already has access to this deck. Create a published version to share with others: a locked copy that won’t change as you keep working.',
    cta: 'Finish up',
  },
} satisfies Record<string, CoachmarkCopy>;

export const GRADUATION = {
  title: 'Welcome to Pitchcrow',
  body: 'AI for the modern investment bank. Now it’s yours.',
  primaryCta: 'Create your first space',
  secondaryCta: 'Keep exploring the demo',
  note: 'The demo space stays in your sidebar. Delete it any time.',
};

export const CHECKLIST_ITEMS = [
  { id: 'explore', label: 'Explore the demo deal room' },
  { id: 'sheet', label: 'Generate the valuation model' },
  { id: 'deck', label: 'Generate the CIM' },
  { id: 'comments', label: 'Resolve deal-team comments' },
  { id: 'space', label: 'Create your own space' },
] as const;

export const SHEET_PROMPT =
  'Build a valuation model for Summit Mechanical Group as a new workbook. Work from the files in this space — the audited financials and FY2026 budget in 01 Financials, the QoE draft for the EBITDA adjustments, and the trading comps and precedent transactions in 04 Valuation Inputs. I want four tabs: assumptions & financials with a five-year projection, trading comps with implied enterprise value, a DCF with a sensitivity grid, and a football field summarizing comps, precedents, DCF and an LBO ability-to-pay view. Standard banking conventions throughout.';

export const COMMENTS_PROMPT =
  'Resolve the comments on the customer slide.';

export const DECK_PROMPT =
  'Create the sell-side pitch for Summit Mechanical Group — this is Project Alpine. Use our house style from the Crow Capital Markets decks in the Templates folder. Build it from the files in this space: company story and operations from the management presentation and service-line materials, market context from the Meridian Insights report, financials from the audits and budget, and the valuation summary from the Project Alpine valuation model workbook — bring the football field into the deck. Cover the business, investment highlights, customers, market, financials, valuation, buyer universe and process timeline, with an appendix. Roughly 30 slides, widescreen.';

// Replay activity steps, phrased the way the live agent narrates.
export const SHEET_REPLAY_STEPS = [
  'Reading Summit Mechanical — Audited Financial Statements FY2023–FY2025',
  'Reading Quality of Earnings Report (Draft) — Whetstone Advisory',
  'Reading FY2026 Budget — Board Approved',
  'Building Assumptions & Financials: five-year projection',
  'Building Trading Comps: median 11.2x',
  'Building DCF: WACC build-up and sensitivity grid',
  'Building Football Field: four methodologies',
];

export const DECK_REPLAY_STEPS = [
  'Reading the Templates folder: Crow Capital Markets house style',
  'Reading Management Presentation and Service Lines Overview',
  'Reading Meridian Insights: US Commercial HVAC Services Outlook 2026',
  'Reading Project Alpine — Valuation Model',
  'Outlining 35 slides across six sections',
  'Writing Business & Operations',
  'Writing Customers & Market',
  'Writing Financial Overview',
  'Writing Valuation & Process: football field from your model',
  'Finalizing appendix and page furniture',
];
